// components/SignUp.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthWrapper from '../AuthWrapper';

const SignUp = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  const validateForm = () => {
    const newErrors = {};

    // Full Name validation
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    } else if (formData.fullName.length < 2 || formData.fullName.length > 100) {
      newErrors.fullName = 'Full name must be between 2 and 100 characters';
    }

    // Username validation
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (formData.username.length < 3 || formData.username.length > 50) {
      newErrors.username = 'Username must be between 3 and 50 characters';
    } else if (!/^[a-zA-Z0-9_]+$/.test(formData.username)) {
      newErrors.username = 'Username can only contain letters, numbers, and underscores';
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters long';
    } else if (!/[A-Z]/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one uppercase letter';
    } else if (!/[a-z]/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one lowercase letter';
    } else if (!/[0-9]/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one number';
    } else if (!/[!@#$%^&*(),.?":{}|<>]/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one special character';
    }

    // Confirm Password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.confirmPassword !== formData.password) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    // Clear success message when user makes changes
    if (success) setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccess('');
    setErrors({});

    if (!validateForm()) return;

    setLoading(true);

    try {
      // Replace with your actual API endpoint
      const response = await fetch('http://localhost:5000/api/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fullName: formData.fullName,
          username: formData.username,
          email: formData.email,
          password: formData.password,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('Account created successfully!');
        // Reset form
        setFormData({
          fullName: '',
          username: '',
          email: '',
          password: '',
          confirmPassword: '',
        });
        // Redirect to sign in after 2 seconds
        setTimeout(() => {
          navigate('/signin');
        }, 2000);
      } else {
        setErrors({ general: data.message || 'Registration failed. Please try again.' });
      }
    } catch (err) {
      setErrors({ general: 'Network error. Please check your connection.' });
    } finally {
      setLoading(false);
    }
  };

  // Password strength indicator
  const getPasswordStrength = () => {
    const pwd = formData.password;
    if (!pwd) return { score: 0, label: '', color: '' };
    
    let score = 0;
    if (pwd.length >= 8) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/[a-z]/.test(pwd)) score++;
    if (/[0-9]/.test(pwd)) score++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) score++;
    
    const strengths = [
      { label: 'Very Weak', color: '#EF4444' },
      { label: 'Weak', color: '#F59E0B' },
      { label: 'Fair', color: '#F59E0B' },
      { label: 'Good', color: '#10B981' },
      { label: 'Strong', color: '#10B981' },
      { label: 'Very Strong', color: '#059669' },
    ];
    
    return { score, ...strengths[score] };
  };

  const passwordStrength = getPasswordStrength();

  return (
    <AuthWrapper>
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: 16 }}>
          <label style={{
            display: 'block',
            fontSize: 13,
            fontWeight: 600,
            color: '#374151',
            marginBottom: 6,
          }}>
            Full Name
          </label>
          <input
            type="text"
            name="fullName"
            value={formData.fullName}
            onChange={handleChange}
            placeholder="Enter your full name"
            style={{
              width: '100%',
              padding: '12px 14px',
              border: `1.5px solid ${errors.fullName ? '#EF4444' : '#D1D5DB'}`,
              borderRadius: 8,
              fontSize: 14,
              transition: 'border-color 0.2s',
              outline: 'none',
            }}
            onFocus={(e) => {
              if (!errors.fullName) e.target.style.borderColor = '#1D9E75';
            }}
            onBlur={(e) => {
              if (!errors.fullName) e.target.style.borderColor = '#D1D5DB';
            }}
          />
          {errors.fullName && (
            <div style={{
              color: '#EF4444',
              fontSize: 12,
              marginTop: 4,
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}>
              <i className="ti ti-alert-circle" style={{ fontSize: 14 }} aria-hidden="true" />
              {errors.fullName}
            </div>
          )}
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{
            display: 'block',
            fontSize: 13,
            fontWeight: 600,
            color: '#374151',
            marginBottom: 6,
          }}>
            Username
          </label>
          <input
            type="text"
            name="username"
            value={formData.username}
            onChange={handleChange}
            placeholder="Choose a username"
            style={{
              width: '100%',
              padding: '12px 14px',
              border: `1.5px solid ${errors.username ? '#EF4444' : '#D1D5DB'}`,
              borderRadius: 8,
              fontSize: 14,
              transition: 'border-color 0.2s',
              outline: 'none',
            }}
            onFocus={(e) => {
              if (!errors.username) e.target.style.borderColor = '#1D9E75';
            }}
            onBlur={(e) => {
              if (!errors.username) e.target.style.borderColor = '#D1D5DB';
            }}
          />
          {errors.username && (
            <div style={{
              color: '#EF4444',
              fontSize: 12,
              marginTop: 4,
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}>
              <i className="ti ti-alert-circle" style={{ fontSize: 14 }} aria-hidden="true" />
              {errors.username}
            </div>
          )}
          <div style={{ fontSize: 11, color: '#6B7280', marginTop: 4 }}>
            <i className="ti ti-info-circle" style={{ marginRight: 4 }} aria-hidden="true" />
            3-50 characters, letters, numbers, and underscores only
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{
            display: 'block',
            fontSize: 13,
            fontWeight: 600,
            color: '#374151',
            marginBottom: 6,
          }}>
            Email
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Enter your email"
            style={{
              width: '100%',
              padding: '12px 14px',
              border: `1.5px solid ${errors.email ? '#EF4444' : '#D1D5DB'}`,
              borderRadius: 8,
              fontSize: 14,
              transition: 'border-color 0.2s',
              outline: 'none',
            }}
            onFocus={(e) => {
              if (!errors.email) e.target.style.borderColor = '#1D9E75';
            }}
            onBlur={(e) => {
              if (!errors.email) e.target.style.borderColor = '#D1D5DB';
            }}
          />
          {errors.email && (
            <div style={{
              color: '#EF4444',
              fontSize: 12,
              marginTop: 4,
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}>
              <i className="ti ti-alert-circle" style={{ fontSize: 14 }} aria-hidden="true" />
              {errors.email}
            </div>
          )}
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{
            display: 'block',
            fontSize: 13,
            fontWeight: 600,
            color: '#374151',
            marginBottom: 6,
          }}>
            Password
          </label>
          <div style={{ position: 'relative' }}>
            <input
              type={showPassword ? 'text' : 'password'}
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Create a password"
              style={{
                width: '100%',
                padding: '12px 14px',
                paddingRight: '48px',
                border: `1.5px solid ${errors.password ? '#EF4444' : '#D1D5DB'}`,
                borderRadius: 8,
                fontSize: 14,
                transition: 'border-color 0.2s',
                outline: 'none',
              }}
              onFocus={(e) => {
                if (!errors.password) e.target.style.borderColor = '#1D9E75';
              }}
              onBlur={(e) => {
                if (!errors.password) e.target.style.borderColor = '#D1D5DB';
              }}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={{
                position: 'absolute',
                right: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#6B7280',
                fontSize: 18,
                padding: 4,
              }}
            >
              <i className={`ti ${showPassword ? 'ti-eye-off' : 'ti-eye'}`} aria-hidden="true" />
            </button>
          </div>
          
          {/* Password Strength Indicator */}
          {formData.password && (
            <div style={{ marginTop: 8 }}>
              <div style={{
                height: 4,
                background: '#E5E7EB',
                borderRadius: 2,
                overflow: 'hidden',
              }}>
                <div style={{
                  height: '100%',
                  width: `${(passwordStrength.score / 5) * 100}%`,
                  background: passwordStrength.color,
                  transition: 'width 0.3s ease',
                }} />
              </div>
              <div style={{
                fontSize: 11,
                color: passwordStrength.color,
                marginTop: 4,
              }}>
                Password Strength: {passwordStrength.label}
              </div>
            </div>
          )}
          
          {errors.password && (
            <div style={{
              color: '#EF4444',
              fontSize: 12,
              marginTop: 4,
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}>
              <i className="ti ti-alert-circle" style={{ fontSize: 14 }} aria-hidden="true" />
              {errors.password}
            </div>
          )}
          <div style={{ fontSize: 11, color: '#6B7280', marginTop: 4 }}>
            <i className="ti ti-info-circle" style={{ marginRight: 4 }} aria-hidden="true" />
            Min 8 chars, 1 uppercase, 1 lowercase, 1 number, 1 special character
          </div>
        </div>

        <div style={{ marginBottom: 24 }}>
          <label style={{
            display: 'block',
            fontSize: 13,
            fontWeight: 600,
            color: '#374151',
            marginBottom: 6,
          }}>
            Confirm Password
          </label>
          <div style={{ position: 'relative' }}>
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Confirm your password"
              style={{
                width: '100%',
                padding: '12px 14px',
                paddingRight: '48px',
                border: `1.5px solid ${errors.confirmPassword ? '#EF4444' : '#D1D5DB'}`,
                borderRadius: 8,
                fontSize: 14,
                transition: 'border-color 0.2s',
                outline: 'none',
              }}
              onFocus={(e) => {
                if (!errors.confirmPassword) e.target.style.borderColor = '#1D9E75';
              }}
              onBlur={(e) => {
                if (!errors.confirmPassword) e.target.style.borderColor = '#D1D5DB';
              }}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              style={{
                position: 'absolute',
                right: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#6B7280',
                fontSize: 18,
                padding: 4,
              }}
            >
              <i className={`ti ${showConfirmPassword ? 'ti-eye-off' : 'ti-eye'}`} aria-hidden="true" />
            </button>
          </div>
          {errors.confirmPassword && (
            <div style={{
              color: '#EF4444',
              fontSize: 12,
              marginTop: 4,
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}>
              <i className="ti ti-alert-circle" style={{ fontSize: 14 }} aria-hidden="true" />
              {errors.confirmPassword}
            </div>
          )}
        </div>

        {errors.general && (
          <div style={{
            padding: '12px 14px',
            background: '#FEE2E2',
            borderRadius: 8,
            marginBottom: 16,
            color: '#DC2626',
            fontSize: 13,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}>
            <i className="ti ti-alert-circle" aria-hidden="true" />
            {errors.general}
          </div>
        )}

        {success && (
          <div style={{
            padding: '12px 14px',
            background: '#D1FAE5',
            borderRadius: 8,
            marginBottom: 16,
            color: '#065F46',
            fontSize: 13,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}>
            <i className="ti ti-check-circle" aria-hidden="true" />
            {success}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          style={{
            width: '100%',
            padding: '12px',
            background: '#1D9E75',
            color: '#ffffff',
            border: 'none',
            borderRadius: 8,
            fontSize: 15,
            fontWeight: 600,
            cursor: loading ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s',
            opacity: loading ? 0.7 : 1,
          }}
          onMouseEnter={(e) => {
            if (!loading) e.currentTarget.style.background = '#167A5A';
          }}
          onMouseLeave={(e) => {
            if (!loading) e.currentTarget.style.background = '#1D9E75';
          }}
        >
          {loading ? (
            <span>
              <i className="ti ti-loader" style={{ animation: 'spin 1s linear infinite', marginRight: 8 }} aria-hidden="true" />
              Creating account...
            </span>
          ) : (
            'Create Account'
          )}
        </button>
      </form>
    </AuthWrapper>
  );
};

export default SignUp;